import { useEffect, useMemo, useState } from "react";
import Editor from "@monaco-editor/react";
import {
  Save,
  FlaskConical,
  Zap,
  History,
  FilePlus,
  FolderPlus,
  Trash2,
  AlertTriangle,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import NginxTree from "@/components/NginxTree";

function detectLang(filename) {
  const n = filename.toLowerCase();
  if (n.endsWith(".conf") || n === "nginx.conf" || n.includes(".conf")) return "nginx";
  if (n.endsWith(".json")) return "json";
  if (n.endsWith(".js")) return "javascript";
  return "plaintext";
}

export default function Nginx() {
  const [tree, setTree] = useState(null);
  const [rootPath, setRootPath] = useState("");
  const [expanded, setExpanded] = useState(new Set(["__root__"]));
  const [activeFile, setActiveFile] = useState(null);
  const [content, setContent] = useState("");
  const [origContent, setOrigContent] = useState("");
  const [err, setErr] = useState("");
  const [notice, setNotice] = useState("");
  const [testOutput, setTestOutput] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showBackups, setShowBackups] = useState(false);
  const [backups, setBackups] = useState([]);

  const loadTree = async () => {
    try {
      const { data } = await api.get("/nginx/tree");
      setTree(data.tree);
      setRootPath(data.root);
    } catch (e) {
      setErr(formatApiError(e));
    }
  };

  useEffect(() => {
    loadTree();
  }, []);

  const toggle = (path) => {
    const next = new Set(expanded);
    if (next.has(path)) next.delete(path);
    else next.add(path);
    setExpanded(next);
  };

  const selectFile = async (node) => {
    setErr("");
    setTestOutput(null);
    try {
      const { data } = await api.get("/nginx/file", { params: { path: node.path } });
      setActiveFile(node);
      setContent(data.content);
      setOrigContent(data.content);
    } catch (e) {
      setErr(formatApiError(e));
    }
  };

  const save = async () => {
    if (!activeFile) return;
    setSaving(true);
    setErr("");
    setNotice("");
    try {
      const { data } = await api.put("/nginx/file", {
        path: activeFile.path,
        content,
      });
      setOrigContent(content);
      setNotice(
        data.backup
          ? `Gespeichert. Backup: ${data.backup.file}`
          : "Gespeichert."
      );
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const runTest = async () => {
    setTestOutput({ loading: true });
    try {
      const { data } = await api.post("/nginx/test");
      setTestOutput(data);
    } catch (e) {
      setTestOutput({ ok: false, stderr: formatApiError(e) });
    }
  };

  const runReload = async () => {
    setNotice("");
    setErr("");
    try {
      await api.post("/nginx/reload");
      setNotice("nginx reload ok");
    } catch (e) {
      setErr(formatApiError(e));
    }
  };

  const loadBackups = async () => {
    try {
      const { data } = await api.get("/nginx/backups");
      setBackups(data.items);
      setShowBackups(true);
    } catch (e) {
      setErr(formatApiError(e));
    }
  };

  const restoreBackup = async (file) => {
    if (!activeFile) {
      setErr("Wähle zuerst die Zieldatei aus (im Baum links).");
      return;
    }
    if (!window.confirm(`Backup "${file}" in "${activeFile.path}" wiederherstellen?`)) return;
    try {
      await api.post("/nginx/backups/restore", {
        backup_file: file,
        target_path: activeFile.path,
      });
      setNotice("Backup wiederhergestellt.");
      selectFile(activeFile);
    } catch (e) {
      setErr(formatApiError(e));
    }
  };

  const addFile = async () => {
    const name = window.prompt("Dateiname (relativ zum ausgewählten Ordner oder Root):");
    if (!name) return;
    const parent = activeFile?.is_dir ? activeFile.path : "";
    try {
      await api.post("/nginx/file", { path: parent, name, content: "", is_dir: false });
      loadTree();
    } catch (e) {
      setErr(formatApiError(e));
    }
  };

  const addDir = async () => {
    const name = window.prompt("Verzeichnis-Name:");
    if (!name) return;
    const parent = activeFile?.is_dir ? activeFile.path : "";
    try {
      await api.post("/nginx/file", { path: parent, name, content: "", is_dir: true });
      loadTree();
    } catch (e) {
      setErr(formatApiError(e));
    }
  };

  const deleteEntry = async () => {
    if (!activeFile) return;
    if (!window.confirm(`"${activeFile.path}" löschen?`)) return;
    try {
      await api.delete("/nginx/file", { params: { path: activeFile.path } });
      setActiveFile(null);
      setContent("");
      setOrigContent("");
      loadTree();
    } catch (e) {
      setErr(formatApiError(e));
    }
  };

  const dirty = content !== origContent;

  return (
    <div data-testid="nginx-page" className="h-full flex flex-col">
      <PageHeader title="Nginx" subtitle={`// ${rootPath || "/etc/nginx"}`} testId="nginx-header">
        <button
          onClick={runTest}
          data-testid="nginx-test-button"
          className="flex items-center gap-2 px-3 py-2 text-xs font-mono uppercase tracking-[0.15em] border border-[#1f1f22] text-[#8b8d98] hover:text-white hover:border-white"
        >
          <FlaskConical className="w-3.5 h-3.5" strokeWidth={1.5} />
          nginx -t
        </button>
        <button
          onClick={runReload}
          data-testid="nginx-reload-button"
          className="flex items-center gap-2 px-3 py-2 text-xs font-mono uppercase tracking-[0.15em] border border-[#22d3ee]/60 text-[#22d3ee] hover:bg-[#22d3ee] hover:text-black"
        >
          <Zap className="w-3.5 h-3.5" strokeWidth={1.5} />
          Reload
        </button>
        <button
          onClick={loadBackups}
          data-testid="nginx-backups-button"
          className="flex items-center gap-2 px-3 py-2 text-xs font-mono uppercase tracking-[0.15em] border border-[#1f1f22] text-[#8b8d98] hover:text-white hover:border-white"
        >
          <History className="w-3.5 h-3.5" strokeWidth={1.5} />
          Backups
        </button>
      </PageHeader>

      {err ? (
        <div className="mx-8 mt-4 text-xs font-mono text-[#dc2626] border border-[#dc2626]/40 bg-[#dc2626]/5 px-3 py-2">
          {err}
        </div>
      ) : null}
      {notice ? (
        <div className="mx-8 mt-4 text-xs font-mono text-[#10b981] border border-[#10b981]/40 bg-[#10b981]/5 px-3 py-2">
          {notice}
        </div>
      ) : null}

      {testOutput ? (
        <div
          className={[
            "mx-8 mt-4 text-[11px] font-mono border px-3 py-2 whitespace-pre-wrap",
            testOutput.loading
              ? "border-[#1f1f22] text-[#8b8d98]"
              : testOutput.ok
              ? "border-[#10b981]/40 bg-[#10b981]/5 text-[#10b981]"
              : "border-[#dc2626]/40 bg-[#dc2626]/5 text-[#dc2626]",
          ].join(" ")}
        >
          {testOutput.loading
            ? "Teste Konfiguration…"
            : (testOutput.stdout || "") + (testOutput.stderr || "")}
        </div>
      ) : null}

      <div className="flex-1 grid grid-cols-12 gap-px bg-[#1f1f22] mx-8 mb-8 mt-4 min-h-[60vh]">
        {/* TREE */}
        <div className="col-span-12 md:col-span-3 bg-[#0a0a0a] overflow-auto max-h-[70vh]">
          <div className="flex items-center gap-1 px-2 py-2 grid-border-b" style={{ borderBottom: "1px solid #1f1f22" }}>
            <button onClick={addFile} data-testid="nginx-new-file" className="p-1 text-[#8b8d98] hover:text-white" title="Neue Datei">
              <FilePlus className="w-3.5 h-3.5" strokeWidth={1.5} />
            </button>
            <button onClick={addDir} data-testid="nginx-new-dir" className="p-1 text-[#8b8d98] hover:text-white" title="Neues Verzeichnis">
              <FolderPlus className="w-3.5 h-3.5" strokeWidth={1.5} />
            </button>
            {activeFile ? (
              <button onClick={deleteEntry} data-testid="nginx-delete" className="p-1 text-[#dc2626] hover:text-white ml-auto" title="Löschen">
                <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} />
              </button>
            ) : null}
          </div>
          {tree ? (
            <NginxTree
              tree={tree}
              onSelect={selectFile}
              activePath={activeFile?.path}
              expanded={expanded}
              toggle={toggle}
            />
          ) : (
            <div className="p-4 text-xs font-mono text-[#8b8d98] animate-pulse">Lade Baum…</div>
          )}
        </div>

        {/* EDITOR */}
        <div className="col-span-12 md:col-span-9 bg-[#0a0a0a] flex flex-col min-h-[60vh]">
          <div
            className="px-4 py-2.5 flex items-center justify-between grid-border-b"
            style={{ borderBottom: "1px solid #1f1f22" }}
          >
            <div className="font-mono text-xs text-white truncate">
              {activeFile ? (
                <>
                  <span className="text-[#8b8d98]">{rootPath}/</span>
                  <span className="text-white">{activeFile.path}</span>
                  {dirty ? <span className="ml-2 text-[#eab308]">●</span> : null}
                </>
              ) : (
                <span className="text-[#8b8d98]">// keine Datei ausgewählt</span>
              )}
            </div>
            <button
              onClick={save}
              disabled={!activeFile || !dirty || saving}
              data-testid="nginx-save-button"
              className="flex items-center gap-2 px-3 py-1.5 text-[11px] font-mono uppercase tracking-[0.15em] border border-[#22d3ee]/60 text-[#22d3ee] hover:bg-[#22d3ee] hover:text-black disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <Save className="w-3.5 h-3.5" strokeWidth={1.5} />
              {saving ? "Speichere…" : "Speichern"}
            </button>
          </div>
          <div className="flex-1 min-h-[50vh]">
            {activeFile ? (
              <Editor
                theme="vs-dark"
                height="100%"
                language={detectLang(activeFile.name)}
                value={content}
                onChange={(v) => setContent(v ?? "")}
                options={{
                  fontFamily: "IBM Plex Mono, monospace",
                  fontSize: 13,
                  minimap: { enabled: false },
                  renderLineHighlight: "gutter",
                  scrollBeyondLastLine: false,
                }}
              />
            ) : (
              <div className="p-10 text-center text-xs font-mono text-[#8b8d98]">
                <AlertTriangle className="w-4 h-4 inline mr-2 opacity-60" strokeWidth={1.5} />
                Wähle eine Datei im Baum links aus.
              </div>
            )}
          </div>
        </div>
      </div>

      {/* BACKUPS DRAWER */}
      {showBackups ? (
        <div className="fixed inset-0 z-40 bg-black/70 flex items-center justify-center p-4" onClick={() => setShowBackups(false)}>
          <div
            onClick={(e) => e.stopPropagation()}
            className="bg-[#0a0a0a] w-full max-w-2xl max-h-[80vh] overflow-auto"
            style={{ border: "1px solid #1f1f22" }}
          >
            <div className="px-4 py-3 flex items-center justify-between" style={{ borderBottom: "1px solid #1f1f22" }}>
              <div className="overline">// backups</div>
              <button
                onClick={() => setShowBackups(false)}
                className="text-[#8b8d98] hover:text-white text-xs font-mono uppercase tracking-[0.15em]"
              >
                schließen
              </button>
            </div>
            <div className="divide-y divide-[#1f1f22]">
              {backups.length ? (
                backups.map((b) => (
                  <div key={b.file} className="flex items-center gap-3 px-4 py-2 font-mono text-[11px]">
                    <div className="flex-1 text-white truncate">{b.file}</div>
                    <div className="text-[#8b8d98]">{(b.size / 1024).toFixed(1)} KB</div>
                    <div className="text-[#8b8d98]">{new Date(b.modified).toLocaleString()}</div>
                    <button
                      onClick={() => restoreBackup(b.file)}
                      data-testid={`nginx-backup-restore-${b.file}`}
                      className="px-2 py-1 border border-[#22d3ee]/60 text-[#22d3ee] hover:bg-[#22d3ee] hover:text-black uppercase tracking-[0.15em] text-[10px]"
                    >
                      Restore
                    </button>
                  </div>
                ))
              ) : (
                <div className="p-6 text-xs font-mono text-[#8b8d98]">Keine Backups.</div>
              )}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
